import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Search, Lock, Save, Wallet } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RegistrationSuccessDialog } from "@/components/AccommodationDialogs";
import { PageHeader, Panel, BrandButton, StandingBadge } from "@/components/PageHeader";
import { useApp } from "@/context/AppContext";
import { IdField, GAMLS_RULE } from "@/components/member/IdField";
import { SdGuestsInput } from "@/components/registration/SdGuestsInput";
import { StandingBanner } from "@/components/member/StandingCard";
import { api, cedi, errMsg, showPin, regClosed, fmtDeadline } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { AccommodationStep, accommodationCost } from "@/components/Accommodation";
import { DraftBanner } from "@/components/RegistrationDraft";
import { ExtrasStep, hasExtras } from "@/components/ExtrasStep";
import { EventSponsors } from "@/components/sponsors/EventSponsors";
import { PROTOCOL, ProtocolPicker, protocolSelection, protocolSubs } from "@/components/ProtocolPicker";
import { fixedCategoriesFor } from "@/lib/categories";

const GOOD = "Good Standing";
const NO_STAY = { hotelId: "", roomId: "", nights: 0 };
const SPONSOR = "sponsor";
const SUCCESS_KEY = "gamls_reg_success";
const SPONSOR_CAT = { id: SPONSOR, name: "Sponsor", fee: 0, good_standing_only: false };

const Choice = ({ active, onClick, children, testId, disabled }) => (
  <button type="button" onClick={onClick} data-testid={testId} disabled={disabled}
    className={`text-left rounded-md border px-4 py-3 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${active ? "border-[#E60000] bg-[#FFF0F0] text-[#111]" : "border-[#E5E7EB] hover:border-gray-400"}`}>
    {children}
  </button>
);

const Verify = ({ pin, setPin, delegate, onVerify, locked, noPin, setNoPin }) => (
  <Panel title="1. Participant Verification" testId="reg-step-verify">
    {!locked && <span className="text-xs font-bold uppercase tracking-wider text-gray-600">{noPin ? "Email or phone number on record" : "AHPC PIN"}</span>}
    <div className="flex gap-2 mt-1.5">
      <Input value={locked ? showPin(pin) : pin} onChange={(e) => setPin(e.target.value)} disabled={locked} aria-label={noPin ? "Email or phone number" : "AHPC PIN"}
        className={`h-10 ${noPin ? "" : "font-mono"}`} data-testid="reg-pin-input" />
      {!locked && <BrandButton variant="dark" onClick={onVerify} data-testid="reg-verify-button"><Search className="w-4 h-4" />Verify</BrandButton>}
    </div>
    {!locked && (
      <label className="mt-2 flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
        <Checkbox checked={noPin} onCheckedChange={(c) => { setNoPin(!!c); setPin(""); }} data-testid="reg-pin-none" />Participant doesn't have an AHPC PIN
      </label>
    )}
    {delegate && (
      <div className="mt-4 flex items-start gap-3 rounded-md bg-emerald-50 border border-emerald-200 p-3 text-sm" data-testid="reg-verified-delegate">
        <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
        <div><div className="font-semibold">{delegate.name}</div><div className="text-gray-600">{[showPin(delegate.pin), delegate.title, delegate.facility, delegate.region].filter(Boolean).join(" · ")}</div></div>
      </div>
    )}
  </Panel>
);

const MembershipIds = ({ ids, setIds, standing, required, locked, desk }) => (
  <Panel title="2. Membership IDs" testId="reg-step-membership" right={<StandingBadge standing={standing} testId="reg-standing-badge" />}>
    <div className="grid sm:grid-cols-2 gap-3">
      {[["gamlsId", "noGamls", "GAMLS ID", "reg-gamls-id"], ["staffId", "noStaff", "Staff ID", "reg-staff-id"]].map(([k, nk, label, tid]) => (
        <div key={k}><span className="text-xs font-bold uppercase tracking-wider text-gray-600">{label}{desk ? " *" : ""}</span>
          <div className="mt-1.5">{desk ? (
            <IdField label={label} value={ids[k]} onChange={(v) => setIds({ [k]: v })} none={ids[nk]} setNone={(v) => setIds({ [nk]: v })}
              noneLabel={`Participant doesn't have a ${label}`} testId={tid} inputClass="h-10" rule={k === "gamlsId" ? GAMLS_RULE : undefined} />
          ) : (
            <Input value={ids[k]} onChange={(e) => setIds({ [k]: e.target.value })} disabled={locked} placeholder={locked ? "Not provided" : undefined} className="h-10 font-mono" data-testid={`${tid}-input`} />
          )}</div>
        </div>
      ))}
    </div>
    <p className="text-xs text-gray-500 mt-3" data-testid="reg-membership-hint">
      {locked ? "Filled in from your member profile and checked against the membership master list."
        : desk ? "Enter both IDs, or tick the box if the participant doesn't have one. Either ID on the master list gives Good Standing."
          : `Enter your GAMLS ID and/or Staff ID${required ? " (required for the selected category)" : ""}. They are checked against the membership master list.`}
    </p>
  </Panel>
);

const useStanding = (gamlsId, staffId) => {
  const [standing, setStanding] = useState(null);
  useEffect(() => {
    if (!gamlsId.trim() && !staffId.trim()) { setStanding(null); return undefined; }
    const t = setTimeout(() => api.post("/membership/check", { gamls_id: gamlsId, staff_id: staffId }).then((r) => setStanding(r.data.standing)), 400);
    return () => clearTimeout(t);
  }, [gamlsId, staffId]);
  return standing;
};

export default function Registration({ lockedPin = "" }) {
  const { events, refresh, member } = useApp();
  const idsLocked = !!lockedPin;
  const [params] = useSearchParams();
  const [pin, setPin] = useState(lockedPin);
  const [noPin, setNoPin] = useState(false);
  const [delegate, setDelegate] = useState(null);
  const [eventId, setEventId] = useState(params.get("event") || events[0]?.id);
  useEffect(() => {
    if (!eventId && events.length) { setEventId(events[0].id); }
  }, [eventId, events]);
  const [cat, setCat] = useState("");
  const [protocolId, setProtocolId] = useState("");
  const [stay, setStay] = useState(NO_STAY);
  const [attendance, setAttendance] = useState("In-Person");
  const [draft, setDraft] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [sdId, setSdId] = useState("");
  const [sdGuests, setSdGuests] = useState([]);
  const [method, setMethod] = useState("MTN MoMo");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved] = useState(() => JSON.parse(sessionStorage.getItem(SUCCESS_KEY) || "null"));
  const [receipt, setReceipt] = useState(saved?.receipt || null);
  const [submittedEvent, setSubmittedEvent] = useState(saved?.event || null);
  useEffect(() => { sessionStorage.removeItem(SUCCESS_KEY); }, []);
  const [ids, setIdsState] = useState({ gamlsId: idsLocked ? member.gamls_id || "" : "", staffId: idsLocked ? member.staff_id || "" : "", noGamls: false, noStaff: false });
  const setIds = (p) => setIdsState((s) => ({ ...s, ...p }));
  const { gamlsId, staffId } = ids;
  const desk = !lockedPin;
  const off = lockedPin ? 2 : 0;
  const checked = useStanding(gamlsId, staffId);
  const noIds = !gamlsId.trim() && !staffId.trim() && (idsLocked || (ids.noGamls && ids.noStaff));
  const standing = noIds ? "Not in Good Standing" : checked;
  const ev = events.find((e) => e.id === eventId);
  const att = ev?.online_attendance ? 1 : 0;
  const online = att === 1 && attendance === "Online";
  const categories = fixedCategoriesFor(ev, standing, desk);
  const catKey = categories.map((c) => c.id).join("|");
  const hasProtocol = protocolSubs(ev).length > 0;
  const hasSponsor = !!ev?.sponsor_category?.enabled;
  const selected = cat === PROTOCOL ? protocolSelection(ev, protocolId) : cat === SPONSOR ? (hasSponsor ? SPONSOR_CAT : null) : categories.find((c) => c.id === cat);
  const deskIdsMissing = desk && ((!gamlsId.trim() && !ids.noGamls) || (!staffId.trim() && !ids.noStaff));
  const idsMissing = deskIdsMissing || (!!selected?.good_standing_only && !gamlsId.trim() && !staffId.trim());
  const notEligible = !!selected?.good_standing_only && !idsMissing && standing !== GOOD;
  const special = cat === PROTOCOL || cat === SPONSOR;
  const sdOption = special ? { id: "included", name: "Souvenir & Dinner (included)", fee: 0 } : hasExtras(ev) ? ev.souvenir_dinner.options.find((o) => o.id === sdId) : null;
  const acc = online ? null : accommodationCost(ev, stay);
  const total = (selected?.fee || 0) + (sdOption?.fee || 0) + (acc?.amount || 0);
  useEffect(() => {
    const ok = (c) => categories.some((x) => x.id === c) || (c === PROTOCOL && protocolSubs(ev).length) || (c === SPONSOR && ev?.sponsor_category?.enabled);
    setCat((c) => (ok(c) ? c : categories[0]?.id || (protocolSubs(ev).length ? PROTOCOL : ev?.sponsor_category?.enabled ? SPONSOR : "")));
  }, [catKey, ev]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setProtocolId((p) => (protocolSubs(ev).some((s) => s.id === p) ? p : ""));
    setStay((s) => {
      const room = ev?.hotels?.find((h) => h.id === s.hotelId)?.rooms.find((r) => r.id === s.roomId);
      if (!s.hotelId || (ev?.accommodation_mode === "booking" && room?.remaining > 0)) return s;
      toast.warning("Your selected room is no longer available. Please choose another.");
      return NO_STAY;
    });
  }, [ev]);
  useEffect(() => {
    const t = setInterval(() => refresh(true), 20000);
    return () => clearInterval(t);
  }, [refresh]);

  const verify = async () => {
    try {
      const r = await api.get(noPin ? "/delegates/find" : `/delegates/by-pin/${encodeURIComponent(pin.trim())}`, noPin ? { params: { q: pin.trim() } } : undefined);
      setDelegate(r.data);
      setPhone(r.data.phone);
      api.get(`/registration-drafts/${encodeURIComponent(r.data.pin)}`).then((d) => setDraft(d.data)).catch(() => setDraft(null));
    } catch (e) {
      setDelegate(null);
      toast.error(errMsg(e));
    }
  };
  useEffect(() => {
    if (lockedPin) { verify(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveDraft = async () => {
    setSavingDraft(true);
    try {
      await api.put(`/registration-drafts/${encodeURIComponent(delegate.pin)}`, { event_id: eventId, data: { eventId, cat, protocolId, sdId, method, phone, gamlsId, staffId, noGamls: ids.noGamls, noStaff: ids.noStaff, stay, attendance } });
      toast.success("Draft saved. Verify your PIN next time to continue.");
    } catch (e) {
      toast.error(errMsg(e));
    }
    setSavingDraft(false);
  };
  const resumeDraft = () => {
    const d = draft.data;
    if (events.some((e) => e.id === d.eventId)) setEventId(d.eventId);
    setCat(d.cat); setProtocolId(d.protocolId || ""); setAttendance(d.attendance || "In-Person"); setSdId(d.sdId || ""); setMethod(d.method || "MTN MoMo"); setPhone(d.phone || "");
    if (!idsLocked) { setIds({ gamlsId: d.gamlsId || "", staffId: d.staffId || "", noGamls: !!d.noGamls, noStaff: !!d.noStaff }); }
    const target = events.find((e) => e.id === d.eventId);
    const room = target?.hotels?.find((h) => h.id === d.stay?.hotelId)?.rooms.find((r) => r.id === d.stay?.roomId);
    const keepStay = d.stay?.hotelId && target?.accommodation_mode === "booking" && room?.remaining > 0;
    setStay(keepStay ? d.stay : NO_STAY);
    if (d.stay?.hotelId && !keepStay) toast.warning("The room in your draft is no longer available. Please choose another.");
    setDraft(null);
    toast.success("Draft restored");
  };
  const discardDraft = async () => {
    await api.delete(`/registration-drafts/${encodeURIComponent(delegate.pin)}`);
    setDraft(null);
    toast("Draft discarded");
  };

  const pay = async () => {
    setBusy(true);
    try {
      const r = await api.post("/registrations", { pin: delegate.pin, event_id: eventId, category_id: cat, protocol_id: cat === PROTOCOL ? protocolId : null, souvenir_dinner_id: special ? null : sdOption?.id || null, gamls_id: gamlsId, staff_id: staffId, no_gamls_id: ids.noGamls, no_staff_id: ids.noStaff,
        attendance: ev?.online_attendance ? attendance : "In-Person", hotel_id: acc ? stay.hotelId : null, room_id: acc ? stay.roomId : null, nights: acc ? stay.nights : 0, sd_guests: special ? [] : sdGuests.filter((x) => x.trim()) });
      if (!r.data.accommodation) {
        sessionStorage.setItem(SUCCESS_KEY, JSON.stringify({ receipt: r.data, event: ev }));
        window.location.reload();
        return;
      }
      setSubmittedEvent(ev);
      setReceipt(r.data);
      setDraft(null);
      setStay(NO_STAY);
      setSdId("");
      setSdGuests([]);
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };

  return (
    <div data-testid="registration-page">
      <PageHeader eyebrow={lockedPin ? "Member registration" : "Checkout"} title="Event Registration" description={`${lockedPin ? "Your details are filled in from your account. Choose" : "Verify the participant by AHPC PIN (or email/phone if they have no PIN), choose"} a package and submit. Payment is made in person at the event, where an Accounts Officer records it and issues your receipt.`} />
      {lockedPin && <StandingBanner member={member} />}
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_380px] gap-4 items-start">
        <div className="space-y-4">
          {!lockedPin && <Verify pin={pin} setPin={setPin} delegate={delegate} onVerify={verify} locked={false} noPin={noPin} setNoPin={setNoPin} />}
          {draft && delegate && <DraftBanner draft={draft} onResume={resumeDraft} onDiscard={discardDraft} />}
          {!lockedPin && <MembershipIds ids={ids} setIds={setIds} standing={standing} required={!!selected?.good_standing_only} locked={false} desk={desk} />}
          {ev?.online_attendance && (
            <Panel title={`${3 - off}. How do you want to attend this event?`} testId="reg-step-attendance">
              <div className="grid grid-cols-2 gap-2">
                {[["In-Person", "At the venue"], ["Online", "Join remotely"]].map(([m, sub]) => (
                  <Choice key={m} active={attendance === m} onClick={() => setAttendance(m)} testId={`reg-attend-${m.toLowerCase()}`}><div className="font-semibold">{m}</div><div className="text-xs text-gray-500 mt-0.5">{sub}</div></Choice>
                ))}
              </div>
            </Panel>
          )}
          <Panel title={`${3 - off + att}. Event & Category`} testId="reg-step-package">
            <Select value={eventId} onValueChange={setEventId}>
              <SelectTrigger className="h-10 min-w-0 [&>span]:truncate" data-testid="reg-event-select"><SelectValue /></SelectTrigger>
              <SelectContent>{events.map((e) => <SelectItem key={e.id} value={e.id}>{e.title}</SelectItem>)}</SelectContent>
            </Select>
            <div className="grid sm:grid-cols-3 gap-2 mt-4">
              {categories.map((c, i) => (
                <Choice key={c.id} active={cat === c.id} onClick={() => setCat(c.id)} testId={`reg-category-${i}`}>
                  <div className="font-semibold">{c.name}</div><div className="font-mono text-xs mt-0.5">{cedi(c.fee)}</div>
                  {c.good_standing_only && <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 mt-1 inline-flex items-center gap-1"><Lock className="w-3 h-3" />Good Standing only</div>}
                </Choice>
              ))}
              {hasProtocol && (
                <Choice active={cat === PROTOCOL} onClick={() => setCat(PROTOCOL)} testId="reg-category-protocol">
                  <div className="font-semibold">Protocol</div><div className="text-xs text-gray-500 mt-0.5">Choose your subcategory</div>
                </Choice>
              )}
              {hasSponsor && (
                <Choice active={cat === SPONSOR} onClick={() => setCat(SPONSOR)} testId="reg-category-sponsor">
                  <div className="font-semibold">Sponsor</div><div className="font-mono text-xs mt-0.5">{cedi(0)}</div>
                </Choice>
              )}
            </div>
            {cat === PROTOCOL && hasProtocol && <ProtocolPicker ev={ev} value={protocolId} onChange={setProtocolId} Choice={Choice} cedi={cedi} />}
            <EventSponsors sponsors={ev?.sponsors} testId="reg-event-sponsors" />
          </Panel>
          {special ? (hasExtras(ev) && <Panel title={`${4 - off + att}. Souvenir & Dinner`} testId="reg-step-extras-included"><p className="text-sm text-emerald-700 font-semibold">Included free for {cat === PROTOCOL ? "Protocol" : "Sponsor"} registrations. No selection needed.</p></Panel>)
            : <><ExtrasStep ev={ev} step={4 - off + att} value={sdOption?.id || ""} onChange={(v) => { setSdId(v); setSdGuests([]); }} />
              <SdGuestsInput option={sdOption} value={sdGuests} onChange={setSdGuests} testId="reg-sd-guests" /></>}
          {!online && <AccommodationStep ev={ev} value={stay} onChange={setStay} step={(hasExtras(ev) ? 5 : 4) - off + att} />}
        </div>
        <Summary ev={ev} selected={selected} sdOption={sdOption} acc={acc} total={total} delegate={delegate} busy={busy} onPay={pay} standing={standing} idsMissing={idsMissing} idsHint={lockedPin ? `${selected?.name || "This category"} is for members in Good Standing only. Choose another category.` : deskIdsMissing ? "Enter both IDs, or tick 'Participant doesn't have' for each one." : "Enter a GAMLS ID or Staff ID for this category."} notEligible={notEligible} onSaveDraft={saveDraft} savingDraft={savingDraft} closed={!!lockedPin && regClosed(ev)} />
      </div>
      <RegistrationSuccessDialog receipt={receipt} event={submittedEvent} onClose={() => setReceipt(null)} />
    </div>
  );
}

const Summary = ({ ev, selected, sdOption, acc, total, delegate, busy, onPay, standing, idsMissing, idsHint, notEligible, onSaveDraft, savingDraft, closed }) => (
  <aside className="bg-[#111] text-white rounded-lg p-6 xl:sticky xl:top-24" data-testid="reg-order-summary">
    <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#FF4D4D]">Order Summary</div>
    <div className="font-heading font-bold mt-2 leading-snug">{ev?.title}</div>
    {(ev?.registration_deadline || closed) && <div className={`text-xs mt-1 ${closed ? "text-[#FF4D4D] font-bold" : "text-white/60"}`} data-testid="reg-deadline-note">{closed ? (ev?.registration_deadline && ev.status !== "Registration Closed" ? `Registration closed on ${fmtDeadline(ev)}` : "Online registration is closed") : `Registration closes ${fmtDeadline(ev)}`}</div>}
    <div className="mt-5 space-y-2 text-sm">
      <div className="flex justify-between text-white/70"><span>{selected?.name || "Category"} fee</span><span className="font-mono">{cedi(selected?.fee)}</span></div>
      {sdOption && <div className="flex justify-between gap-3 text-white/70" data-testid="reg-summary-sd"><span>{sdOption.name}</span><span className="font-mono whitespace-nowrap">{cedi(sdOption.fee)}</span></div>}
      {acc && <div className="flex justify-between gap-3 text-white/70" data-testid="reg-summary-accommodation"><span>{acc.hotel} · {acc.room} × {acc.nights}</span><span className="font-mono whitespace-nowrap">{cedi(acc.amount)}</span></div>}
      <div className="flex justify-between items-center text-white/70"><span>Membership</span>{standing ? <StandingBadge standing={standing} testId="reg-summary-standing" /> : <span>—</span>}</div>
    </div>
    <div className="border-t border-white/15 mt-5 pt-4 flex items-end justify-between">
      <span className="text-sm text-white/60">Total cost</span>
      <span className="font-heading text-3xl font-extrabold" data-testid="reg-total-amount">{cedi(total)}</span>
    </div>
    <p className="mt-3 text-xs text-white/60 flex gap-2" data-testid="reg-pay-at-event-note"><Wallet className="w-4 h-4 shrink-0 text-[#FF4D4D]" />Online payment is not available yet. Pay at the event; the Accounts Officer will record it and print your receipt.</p>
    <BrandButton className="w-full mt-5 h-11" disabled={!delegate || busy || idsMissing || notEligible || !selected || closed} onClick={onPay} data-testid="reg-pay-button">
      {busy ? "Submitting…" : "Submit registration"}
    </BrandButton>
    <button onClick={onSaveDraft} disabled={!delegate || savingDraft || closed} className="w-full mt-2 h-10 rounded-md border border-white/25 text-sm font-semibold text-white/90 hover:bg-white/10 transition-colors disabled:opacity-40 inline-flex items-center justify-center gap-2" data-testid="reg-save-draft-button">
      <Save className="w-4 h-4" />{savingDraft ? "Saving…" : "Save as draft"}
    </button>
    {!delegate && <p className="text-xs text-white/50 mt-3">Verify the participant to continue.</p>}
    {delegate && idsMissing && <p className="text-xs text-white/50 mt-3" data-testid="reg-ids-required-hint">{idsHint}</p>}
    {delegate && notEligible && <p className="text-xs text-[#FF4D4D] mt-3" data-testid="reg-not-eligible-hint">{selected.name} is for members in Good Standing only. Choose another category.</p>}
  </aside>
);
